import { useState } from "react";
import { Input } from "@/components/ui/input";
import CropModal from "@/components/editorial/CropModal";
import { ImageSlot, MediaLibraryPicker } from "@/components/editorial/EditorialMediaControls";
import { useEditorialMediaUrl } from "@/lib/useEditorialMediaUrl";
import { editorialImageStyle } from "@/lib/editorialReaderLayout.mjs";
import { editorialMediaFrameAspect, editorialMediaFrameLabel } from "@/lib/editorialMediaFrame.mjs";

export const HERO_PALETTES = [
  { key: "ivory", label: "Тёплый молочный", swatch: "#EFE6D5" },
  { key: "almond", label: "Миндальный", swatch: "#D6B98A" },
  { key: "olive", label: "Оливковый", swatch: "#7E8A3E" },
  { key: "cocoa", label: "Какао", swatch: "#5B3B25" },
  { key: "burgundy", label: "Бордовый", swatch: "#7A2A2A" },
  { key: "gold", label: "Старинное золото", swatch: "#C9A84C" },
  { key: "pistachio", label: "Фисташковый", swatch: "#A6C088" },
  { key: "apricot", label: "Абрикосовый", swatch: "#E8B48A" },
];

export const HERO_BACKGROUNDS = [
  { key: "warm-ivory", label: "Тёплый молочный", css: "#EFE6D5" },
  { key: "mineral-plaster", label: "Минеральная штукатурка", css: "#E6DFCF" },
  { key: "almond-paper", label: "Миндальная бумага", css: "#F1E7D2" },
  { key: "deep-cocoa", label: "Глубокий какао", css: "#2A1810" },
  { key: "full-image", label: "Фото на весь разворот", css: "transparent" },
];

export const HERO_VARIANTS = [
  { key: "A", label: "Фото слева / текст справа" },
  { key: "B", label: "Текст слева / фото справа" },
  { key: "C", label: "Фото на весь разворот / текст поверх" },
];

const paletteByKey = (key) => HERO_PALETTES.find((item) => item.key === key) || HERO_PALETTES[0];
const backgroundByKey = (key) => HERO_BACKGROUNDS.find((item) => item.key === key) || HERO_BACKGROUNDS[0];
const serif = "'Cormorant Garamond', 'Playfair Display', serif";

function VariantThumb({ variant }) {
  const photo = "bg-espresso/20 rounded";
  const line = "bg-espresso/40 rounded h-1";
  if (variant === "A") return <div className="grid grid-cols-[3fr_2fr] gap-1.5 h-14"><div className={photo} /><div className="flex flex-col justify-center gap-1"><div className={`${line} w-3/4`} /><div className={`${line} w-full`} /><div className={`${line} w-2/3`} /></div></div>;
  if (variant === "B") return <div className="grid grid-cols-[2fr_3fr] gap-1.5 h-14"><div className="flex flex-col justify-center gap-1"><div className={`${line} w-3/4`} /><div className={`${line} w-full`} /><div className={`${line} w-2/3`} /></div><div className={photo} /></div>;
  return <div className="relative h-14"><div className={`${photo} absolute inset-0`} /><div className="absolute inset-x-2 bottom-2 space-y-1"><div className="h-1 w-3/4 rounded bg-white/80" /><div className="h-1 w-1/2 rounded bg-white/80" /></div></div>;
}

export function HeroEditor({ block, onPatch }) {
  const content = block.content || {};
  const settings = block.settings || {};
  const variant = settings.variant || "A";
  const [libraryFor, setLibraryFor] = useState(null);
  const [cropFor, setCropFor] = useState(null);

  const patchContent = (patch) => onPatch({ content: { ...content, ...patch } });
  const patchSettings = (patch) => onPatch({ settings: { ...settings, ...patch } });

  const acceptMedia = (media, slot) => {
    if (slot === "mobile") {
      patchContent({ hero_mobile_media_id: media.id, hero_mobile_url: media.file_url || media.url });
    } else {
      patchContent({ hero_media_id: media.id, hero_url: media.file_url || media.url });
    }
    setLibraryFor(null);
  };

  const saveCrop = ({ focal_x, focal_y, zoom }) => {
    if (cropFor === "mobile") {
      patchSettings({ mobile_focal_x: focal_x, mobile_focal_y: focal_y, hero_mobile_zoom: zoom });
    } else {
      patchSettings({ focal_x, focal_y, hero_zoom: zoom });
    }
    setCropFor(null);
  };

  const desktopAspect = editorialMediaFrameAspect("HERO", { mode: "desktop", variant });
  const mobileAspect = editorialMediaFrameAspect("HERO", { mode: "mobile", variant });

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" data-testid="hero-editor-clean">
      <div className="rounded-2xl border hairline bg-white p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Содержание</div>
        <div><label className="block text-[11px] opacity-60 mb-1">Надзаголовок</label><Input value={content.eyebrow || ""} onChange={(e) => patchContent({ eyebrow: e.target.value })} /></div>
        <div><label className="block text-[11px] opacity-60 mb-1">Заголовок</label><Input value={content.headline || ""} onChange={(e) => patchContent({ headline: e.target.value })} className="font-serif text-lg" /></div>
        <div><label className="block text-[11px] opacity-60 mb-1">Подзаголовок</label><Input value={content.subtitle || ""} onChange={(e) => patchContent({ subtitle: e.target.value })} /></div>
        <div><label className="block text-[11px] opacity-60 mb-1">Короткий лид</label><textarea value={content.lead || ""} rows={3} onChange={(e) => patchContent({ lead: e.target.value })} className="w-full rounded border px-2 py-1.5 text-sm italic" /></div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 pt-2">
          <ImageSlot
            label="Основное фото"
            url={content.hero_url}
            focalX={settings.focal_x ?? 0.5}
            focalY={settings.focal_y ?? 0.5}
            zoom={settings.hero_zoom || 1}
            frameAspect={desktopAspect}
            frameLabel={editorialMediaFrameLabel("HERO", { mode: "desktop", variant })}
            onFile={(media) => patchContent({ hero_media_id: media.id, hero_url: media.file_url || media.url })}
            onClear={() => patchContent({ hero_media_id: null, hero_url: null })}
            onOpenLibrary={() => setLibraryFor("desktop")}
            onOpenCrop={() => setCropFor("desktop")}
            testid="hero-image-desktop"
          />
          <ImageSlot
            label="Фото для телефона"
            url={content.hero_mobile_url || content.hero_url}
            focalX={settings.mobile_focal_x ?? settings.focal_x ?? 0.5}
            focalY={settings.mobile_focal_y ?? settings.focal_y ?? 0.5}
            zoom={settings.hero_mobile_zoom || settings.hero_zoom || 1}
            frameAspect={mobileAspect}
            frameLabel={editorialMediaFrameLabel("HERO", { mode: "mobile", variant })}
            onFile={(media) => patchContent({ hero_mobile_media_id: media.id, hero_mobile_url: media.file_url || media.url })}
            onClear={() => patchContent({ hero_mobile_media_id: null, hero_mobile_url: null })}
            onOpenLibrary={() => setLibraryFor("mobile")}
            onOpenCrop={() => setCropFor("mobile")}
            testid="hero-image-mobile"
          />
        </div>

        <div><label className="block text-[11px] opacity-60 mb-1">Подпись к фото · необязательно</label><Input value={content.caption || ""} onChange={(e) => patchContent({ caption: e.target.value })} /></div>
        <div><label className="block text-[11px] opacity-60 mb-1">Описание изображения · необязательно</label><Input value={content.alt || ""} onChange={(e) => patchContent({ alt: e.target.value })} /></div>
      </div>

      <div className="rounded-2xl border hairline bg-white p-4 space-y-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest opacity-70 mb-2">Композиция</div>
          <div className="grid grid-cols-3 gap-2">
            {HERO_VARIANTS.map((item) => (
              <button key={item.key} type="button" onClick={() => patchSettings({ variant: item.key })}
                className={`rounded-xl border p-3 text-left transition ${variant === item.key ? "border-espresso bg-espresso/5" : "border-espresso/15 hover:bg-cream/60"}`}>
                <VariantThumb variant={item.key} />
                <div className="mt-2 text-[10px] uppercase tracking-widest opacity-70">Вариант {item.key}</div>
                <div className="mt-1 text-[11px] leading-snug">{item.label}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest opacity-70 mb-2">Акцентный цвет</div>
          <div className="flex flex-wrap gap-2">
            {HERO_PALETTES.map((item) => (
              <button key={item.key} type="button" onClick={() => patchSettings({ palette: item.key })}
                className={`h-8 w-8 rounded-full border-2 ${settings.palette === item.key ? "border-espresso scale-110" : "border-white shadow"}`}
                title={item.label} style={{ background: item.swatch }} />
            ))}
          </div>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-widest opacity-70 mb-1">Фон</label>
          <select value={settings.background || "warm-ivory"} onChange={(e) => patchSettings({ background: e.target.value })}
            className="w-full h-10 rounded-md border border-espresso/25 bg-white px-3 text-sm outline-none focus:border-espresso">
            {HERO_BACKGROUNDS.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
          </select>
        </div>

        <label className="flex items-center gap-3"><input type="checkbox" checked={!!settings.botanical} onChange={(e) => patchSettings({ botanical: e.target.checked })} /><span className="text-sm">Ботаническая декоративная деталь</span></label>
        <div className="border-t border-espresso/10 pt-3 text-[10px] opacity-55">
          Desktop и Mobile используют один контент, но имеют независимый кадр и композиционную геометрию.
        </div>
      </div>

      <MediaLibraryPicker open={!!libraryFor} onClose={() => setLibraryFor(null)} onPick={(media) => acceptMedia(media, libraryFor)} />
      <CropModal
        open={!!cropFor}
        mode={cropFor || "desktop"}
        url={cropFor === "mobile" ? (content.hero_mobile_url || content.hero_url) : content.hero_url}
        aspectRatio={cropFor === "mobile" ? mobileAspect : desktopAspect}
        frameLabel={editorialMediaFrameLabel("HERO", { mode: cropFor === "mobile" ? "mobile" : "desktop", variant })}
        focalX={cropFor === "mobile" ? (settings.mobile_focal_x ?? settings.focal_x ?? 0.5) : (settings.focal_x ?? 0.5)}
        focalY={cropFor === "mobile" ? (settings.mobile_focal_y ?? settings.focal_y ?? 0.5) : (settings.focal_y ?? 0.5)}
        zoom={cropFor === "mobile" ? (settings.hero_mobile_zoom || settings.hero_zoom || 1) : (settings.hero_zoom || 1)}
        onSave={saveCrop}
        onClose={() => setCropFor(null)}
      />
    </div>
  );
}

function HeroText({ content, accent, color, mobile = false }) {
  return (
    <div className="min-w-0" style={{ color }}>
      {content.eyebrow && <div className="mb-5 text-[9px] uppercase tracking-[0.42em] opacity-78">{content.eyebrow}</div>}
      <h1 className="font-serif font-normal leading-[0.96] [overflow-wrap:normal] [word-break:normal] [text-wrap:balance]"
        style={{ fontFamily: serif, fontSize: mobile ? "clamp(38px, 11vw, 50px)" : "clamp(50px, 5vw, 72px)" }}>
        {content.headline || ""}
      </h1>
      <div className="mt-5 flex items-center gap-2"><span className="h-px w-10" style={{ background: accent }} /><span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} /></div>
      {content.subtitle && <div className="mt-4 max-w-[32ch] text-[17px] italic leading-[1.45] opacity-85">{content.subtitle}</div>}
      {content.lead && <p className="mt-5 max-w-[38ch] text-[15px] leading-[1.75] opacity-90">{content.lead}</p>}
      {content.caption && <div className="mt-5 text-[10px] italic opacity-55">{content.caption}</div>}
    </div>
  );
}

function renderImage(src, media, alt, className = "") {
  if (!src) return null;
  return <img src={src} alt={alt || ""} className={`h-full w-full ${className}`} style={editorialImageStyle(media, { intentionalCrop: true })} />;
}

export function HeroRender({ block, mode = "desktop" }) {
  const content = block?.content || {};
  const settings = block?.settings || {};
  const variant = settings.variant || "A";
  const palette = paletteByKey(settings.palette);
  const background = backgroundByKey(settings.background);
  const fullBleed = variant === "C" || settings.background === "full-image";
  const bg = background.css === "transparent" ? "#EFE6D5" : background.css;
  const dark = settings.background === "deep-cocoa" || fullBleed;
  const textColor = dark ? "#F4E8D2" : "#3B241A";

  const desktopSrc = useEditorialMediaUrl(content.hero_url, content.hero_media_id);
  const mobileOwnSrc = useEditorialMediaUrl(content.hero_mobile_url, content.hero_mobile_media_id);
  const mobileSrc = mobileOwnSrc || desktopSrc;
  const desktopMedia = { focal_x: settings.focal_x ?? 0.5, focal_y: settings.focal_y ?? 0.5, zoom: settings.hero_zoom || 1 };
  const mobileMedia = { focal_x: settings.mobile_focal_x ?? settings.focal_x ?? 0.5, focal_y: settings.mobile_focal_y ?? settings.focal_y ?? 0.5, zoom: settings.hero_mobile_zoom || settings.hero_zoom || 1 };

  if (mode === "mobile") {
    if (fullBleed && mobileSrc) {
      return (
        <section className="relative min-h-[720px] w-full overflow-hidden bg-[#2A1810]" data-testid="hero-render-mobile-clean">
          <div className="absolute inset-0">{renderImage(mobileSrc, mobileMedia, content.alt, "object-cover")}</div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/72 via-black/12 to-black/8" />
          <div className="absolute inset-x-0 bottom-0 px-7 pb-10 pt-28">
            <HeroText content={content} accent={palette.swatch} color="#F6EAD7" mobile />
          </div>
        </section>
      );
    }

    return (
      <section className="w-full overflow-hidden" style={{ background: bg }} data-testid="hero-render-mobile-clean">
        {mobileSrc && <div className="mx-auto w-full max-w-[360px] overflow-hidden" style={{ aspectRatio: editorialMediaFrameAspect("HERO", { mode: "mobile", variant }) }}>{renderImage(mobileSrc, mobileMedia, content.alt, "object-cover")}</div>}
        <div className="px-7 py-8"><HeroText content={content} accent={palette.swatch} color={textColor} mobile /></div>
      </section>
    );
  }

  if (fullBleed && desktopSrc) {
    return (
      <section className="relative mx-auto w-full max-w-[1180px] overflow-hidden bg-[#2A1810]" style={{ aspectRatio: editorialMediaFrameAspect("HERO", { mode: "desktop", variant: "C" }), minHeight: 560 }} data-testid="hero-render-desktop-clean">
        <div className="absolute inset-0">{renderImage(desktopSrc, desktopMedia, content.alt, "object-cover")}</div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/8 via-black/10 to-black/72" />
        <div className="absolute inset-y-0 right-0 flex w-[46%] items-center px-12 py-14"><HeroText content={content} accent={palette.swatch} color="#F6EAD7" /></div>
      </section>
    );
  }

  const image = desktopSrc ? (
    <div className="flex items-center justify-center px-6 py-8">
      <div className="w-full max-w-[460px] overflow-hidden" style={{ aspectRatio: editorialMediaFrameAspect("HERO", { mode: "desktop", variant }) }}>
        {renderImage(desktopSrc, desktopMedia, content.alt, "object-cover")}
      </div>
    </div>
  ) : <div />;
  const copy = <div className="flex min-w-0 items-center px-8 py-12 lg:px-12 lg:py-14"><HeroText content={content} accent={palette.swatch} color={textColor} /></div>;

  return (
    <section className="mx-auto w-full max-w-[1180px] overflow-hidden" style={{ background: bg }} data-testid="hero-render-desktop-clean">
      <div className="grid min-h-[610px] items-stretch lg:grid-cols-2">{variant === "B" ? <>{copy}{image}</> : <>{image}{copy}</>}</div>
    </section>
  );
}
